import type { ReactElement, ReactNode } from 'react'
import type { RoleId, WithRoleId } from '@terros/common'
import { Text } from '@mantine/core'
import { getInitialism, useRole } from '@front/model'
import { type InfoFetcherProps, type EntityAvatarProps } from './types'
import { LoadingEntityInfo, PrefetchedEntityInfo } from './EntityInfo'
import { EntityHoverCard } from './EntityHoverCard'

type RoleInfoProps = InfoFetcherProps & WithRoleId

export const RoleInfo = ({ roleId, linked, ...avatarProps }: RoleInfoProps): ReactNode => {
  const { data, isLoading, error } = useRole({ roleId })
  if (error) return <Text className='text-red-400'>{error}</Text>
  if (isLoading || !data) return <LoadingEntityInfo {...avatarProps} />

  return <PrefetchedRoleInfo {...avatarProps} linked={linked} role={data.role} />
}

type RoleInfoPrefetchedInput = InfoFetcherProps & {
  companyName?: string
  role: Pick<EntityAvatarProps, 'name'> & {
    roleId: RoleId
  }
}

export const PrefetchedRoleInfo = (props: RoleInfoPrefetchedInput): ReactElement => {
  const { role, linked = true, ...rest } = props

  const { roleId, name } = role
  const initials = getInitialism(name)
  const entityInfo = {
    ...rest,
    name,
    initials,
  }

  const content = <PrefetchedEntityInfo {...entityInfo} />
  if (linked) {
    return (
      <EntityHoverCard
        name={name}
        initials={initials}
        href={`/settings/role/${roleId}`}
        metadata={[{ icon: 'badge', label: 'Role ID', value: roleId, copyable: true }]}
      >
        {content}
      </EntityHoverCard>
    )
  }
  return content
}
